type TimersRef = {
  current: number[];
};

type LastRunRef = {
  current: number;
};

export const clearScheduledRevalidations = (timersRef: TimersRef) => {
  timersRef.current.forEach(window.clearTimeout);
  timersRef.current = [];
};

export const scheduleRevalidations = (
  mutate: () => void,
  timersRef: TimersRef,
  lastRunRef: LastRunRef,
  {
    delays = [2000, 8000],
    throttleMs = 5000,
    includeImmediate = true,
  }: {
    delays?: number[];
    throttleMs?: number;
    includeImmediate?: boolean;
  } = {}
) => {
  const now = Date.now();
  if (now - lastRunRef.current < throttleMs) {
    return;
  }

  lastRunRef.current = now;
  clearScheduledRevalidations(timersRef);

  if (includeImmediate) {
    mutate();
  }

  timersRef.current = delays.map((delay) =>
    window.setTimeout(() => {
      mutate();
    }, delay)
  );
};

// ── Debounced realtime revalidation (FE-9) ──────────────────────────────────

export const REALTIME_REVALIDATE_WAIT_MS = 750;
export const REALTIME_REVALIDATE_MAX_WAIT_MS = 3000;

type DebouncedRevalidation = {
  firstAt: number;
  timer: number;
  run: () => void;
};

/** The owner used when a caller passes none: every such call on a key shares one slot. */
const SHARED_OWNER = {};

// key → owner → pending run.
const debouncedRevalidations = new Map<string, Map<unknown, DebouncedRevalidation>>();

/**
 * One trailing-edge revalidation per (key, owner).
 *
 * A burst of realtime events used to fan out into a ladder of refetches per
 * event per mounted consumer (4 `/account` requests for one event, 3 refetches
 * of every loaded history page). Every call for the same key and owner now
 * pushes a single pending run back by `waitMs`, but never past `maxWaitMs`
 * after the first call of the burst, so a steady stream still refreshes. The
 * latest `run` for that pair wins.
 *
 * `owner` is what the run refreshes — typically the SWR `mutate` (L3). Two
 * tables that share a key but not a cache (e.g. the same query at different
 * page sizes) each keep their own pending run instead of the last one to
 * schedule starving the other.
 */
export const debounceRevalidation = (
  key: string,
  run: () => void,
  {
    waitMs = REALTIME_REVALIDATE_WAIT_MS,
    maxWaitMs = REALTIME_REVALIDATE_MAX_WAIT_MS,
    owner = SHARED_OWNER,
  }: { waitMs?: number; maxWaitMs?: number; owner?: unknown } = {}
) => {
  const now = Date.now();
  const owners = debouncedRevalidations.get(key) || new Map<unknown, DebouncedRevalidation>();
  const existing = owners.get(owner);
  if (existing) window.clearTimeout(existing.timer);

  const firstAt = existing ? existing.firstAt : now;
  const delay = Math.max(0, Math.min(waitMs, firstAt + maxWaitMs - now));
  const entry: DebouncedRevalidation = { firstAt, run, timer: 0 };
  entry.timer = window.setTimeout(() => {
    const current = debouncedRevalidations.get(key);
    if (current?.get(owner) === entry) {
      current.delete(owner);
      if (!current.size) debouncedRevalidations.delete(key);
    }
    entry.run();
  }, delay);
  owners.set(owner, entry);
  debouncedRevalidations.set(key, owners);
};

export const hasPendingRevalidation = (key: string) =>
  debouncedRevalidations.has(key);

export const cancelDebouncedRevalidation = (key: string) => {
  const owners = debouncedRevalidations.get(key);
  if (!owners) return;
  owners.forEach((entry) => window.clearTimeout(entry.timer));
  debouncedRevalidations.delete(key);
};

/** Test hook. */
export const clearDebouncedRevalidations = () => {
  debouncedRevalidations.forEach((owners) =>
    owners.forEach((entry) => window.clearTimeout(entry.timer))
  );
  debouncedRevalidations.clear();
};
